/**
 * AIShield Customer Scoped Dashboard (Stage 6B)
 * Scoped strictly to the authenticated account's selected website_id.
 * Enforces:
 * 1. Selected website_id in telemetry requests and request detail lookups.
 * 2. Immediate data clearance, request cancellation, drawer close, and pagination reset on website switch.
 * 3. Separate protection status from management API connectivity status.
 * 4. Honest DRAFT notice (never claims protection for draft sites).
 * 5. Returns to auth on 401 unauthenticated without silent fallback to demo or synthetic data.
 */

import React, { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { Header } from "./Header.tsx";
import { PrimaryNavigation, type ViewType } from "./PrimaryNavigation.tsx";
import { useNavigationState } from "../../hooks/useNavigationState.ts";
import { OverviewView } from "../views/OverviewView.tsx";
import { RequestsView } from "../views/RequestsView.tsx";
import type {
  TimeRangeKey,
  ShortcutFilter,
  DecisionFilter,
  InspectionFilter,
  SortField,
  SortOrder,
  BundleQueryParams,
  SidebarCounts,
  RequestRecord,
  WebsiteRecord,
} from "../../types/telemetry.ts";
import { useTelemetryPolling } from "../../hooks/useTelemetryPolling.ts";
import { useManagementHealth } from "../../hooks/useManagementHealth.ts";
import { useRequestDetail } from "../../hooks/useRequestDetail.ts";
import { useAuth } from "../../hooks/useAuth.tsx";
import { AlertTriangle } from "lucide-react";
import { WebsiteBadge } from "../websites/WebsiteBadge.tsx";

interface CustomerDashboardProps {
  websites: WebsiteRecord[];
  selectedWebsite: WebsiteRecord;
  onSelectWebsite: (website: WebsiteRecord) => void;
  onAddWebsite?: () => void;
  onResumeSetup?: (website: WebsiteRecord) => void;
  onSwitchToDemo?: () => void;
}

export const CustomerDashboard: React.FC<CustomerDashboardProps> = ({
  websites,
  selectedWebsite,
  onSelectWebsite,
  onAddWebsite,
  onResumeSetup,
  onSwitchToDemo: _onSwitchToDemo,
}) => {
  const { account, logout } = useAuth();
  const [activeView, setActiveView] = useState<ViewType>("overview");
  const {
    isPinned,
    togglePinned,
    isMobileOpen,
    openMobile,
    closeMobile,
  } = useNavigationState();

  // Authoritative query state scoped to selected website
  const [timeRange, setTimeRange] = useState<TimeRangeKey>("60m");
  const [shortcut, setShortcut] = useState<ShortcutFilter>("all");
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [decisionFilter, setDecisionFilter] = useState<DecisionFilter>("ALL");
  const [inspectionFilter, setInspectionFilter] = useState<InspectionFilter>("ALL");
  const [sortField, setSortField] = useState<SortField>("timestamp");
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [page, setPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(10);

  // Selection state for request inspection drawer
  const [selectedRequestId, setSelectedRequestId] = useState<string | null>(null);

  // Debounce search input by 300ms
  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 300);
    return () => clearTimeout(handler);
  }, [searchQuery]);

  // CRITICAL: On website change, cancel detail drawer, reset pagination to 1
  useEffect(() => {
    setSelectedRequestId(null);
    setPage(1);
  }, [selectedWebsite.id]);

  // Reset page to 1 whenever filters change
  const isFirstMount = useRef(true);
  useEffect(() => {
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }
    setPage(1);
  }, [
    timeRange,
    shortcut,
    debouncedSearch,
    decisionFilter,
    inspectionFilter,
    sortField,
    sortOrder,
    pageSize,
  ]);

  // Authoritative query params strictly including websiteId
  const queryParams: BundleQueryParams = useMemo(
    () => ({
      websiteId: selectedWebsite.id,
      demoEndpoint: false,
      timeRange,
      shortcut,
      search: debouncedSearch.trim() || undefined,
      decision: decisionFilter,
      inspectionStatus: inspectionFilter,
      sortField,
      sortOrder,
      page,
      pageSize,
    }),
    [
      selectedWebsite.id,
      timeRange,
      shortcut,
      debouncedSearch,
      decisionFilter,
      inspectionFilter,
      sortField,
      sortOrder,
      page,
      pageSize,
    ]
  );

  // Scoped Telemetry Polling Hook with automatic session expiry handling
  const livePolling = useTelemetryPolling(queryParams, {
    enabled: true,
    onAuthExpired: () => {
      logout();
    },
  });

  const liveHealth = useManagementHealth({ autoPoll: true, enabled: true });
  // Request detail hook scoped to selected website
  const liveDetail = useRequestDetail(selectedRequestId, selectedWebsite.id, false);

  const summary = livePolling.data?.summary ?? null;
  const chart = livePolling.data?.chart ?? null;
  const sidebarCounts: SidebarCounts | null = livePolling.data?.sidebarCounts ?? null;
  const tableItems: RequestRecord[] = livePolling.data?.table.items ?? [];
  const tableTotal: number | null = livePolling.data?.table.total ?? null;
  const totalPages: number | null = livePolling.data?.table.totalPages ?? null;
  const serverTime = livePolling.serverTime;

  const isLoading = livePolling.isLoading;
  const isRefreshing = livePolling.isRefreshing;
  const isStale = livePolling.isStale;
  const hasNewData = livePolling.hasNewData;
  const pollingError = livePolling.error;

  const handleRefresh = useCallback(async (): Promise<boolean> => {
    return await livePolling.refresh();
  }, [livePolling]);

  const isDraftWebsite =
    selectedWebsite.onboarding_status === "DRAFT" ||
    selectedWebsite.onboarding_status === "PENDING_OWNERSHIP";

  return (
    <div className="min-h-screen bg-canvas flex flex-col font-sans text-foreground">
      {/* 48px Header with Website Selector and separate Health/Protection badges */}
      <Header
        isSyntheticMode={false}
        isDemoMode={false}
        serverTime={serverTime}
        onOpenMobileNav={openMobile}
        selectedWebsite={selectedWebsite}
        websites={websites}
        onSelectWebsite={onSelectWebsite}
        onAddWebsite={onAddWebsite}
        customerEmail={account?.email}
        onLogout={() => logout()}
        onSwitchToCustomer={undefined}
        health={{
          isHealthy: liveHealth.isHealthy,
          isConnected: liveHealth.isConnected,
          status: liveHealth.status,
          database: liveHealth.database,
          isLoading: liveHealth.isLoading,
          error: liveHealth.error,
        }}
      />

      {/* Draft Status Banner: Honest indicator that site is not protected yet */}
      {isDraftWebsite && (
        <div
          data-testid="draft-website-notice"
          className="bg-amber-500/10 border-b border-amber-500/20 px-4 py-2 flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-xs text-amber-300 select-none"
        >
          <div className="flex items-center gap-2 min-w-0">
            <AlertTriangle className="w-4 h-4 shrink-0 text-amber-400" />
            <span className="truncate">
              <strong>Domain setup pending:</strong> {selectedWebsite.public_hostname} is in{" "}
              <span className="font-mono">{selectedWebsite.onboarding_status}</span> status. Inbound traffic is not yet protected. Telemetry is currently empty.
            </span>
          </div>
          <div className="flex items-center gap-2.5 shrink-0 self-end sm:self-auto">
            {onResumeSetup && (
              <button
                type="button"
                data-testid="resume-setup-banner-btn"
                onClick={() => onResumeSetup(selectedWebsite)}
                className="px-2.5 py-1 text-xs font-mono font-medium rounded bg-amber-500/20 hover:bg-amber-500/30 text-amber-200 border border-amber-500/40 transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-amber-400"
              >
                Resume Setup →
              </button>
            )}
            <WebsiteBadge status={selectedWebsite.onboarding_status} />
          </div>
        </div>
      )}

      {/* Main Container below Header */}
      <div className="flex flex-grow h-[calc(100vh-48px)] overflow-hidden">
        {/* Collapsible Primary Navigation */}
        <PrimaryNavigation
          activeView={activeView}
          onSelectView={setActiveView}
          isPinned={isPinned}
          onTogglePinned={togglePinned}
          isMobileOpen={isMobileOpen}
          onCloseMobile={closeMobile}
        />

        {/* Dynamic Main Workspace Canvas */}
        <main className="flex-grow min-w-0 overflow-auto bg-canvas flex flex-col">
          {activeView === "overview" ? (
            <OverviewView
              websiteId={selectedWebsite.id}
              summary={summary}
              chart={chart}
              activeTimeRange={timeRange}
              onSelectTimeRange={setTimeRange}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
              isStale={isStale}
              serverTime={serverTime}
              error={pollingError}
              onRefresh={handleRefresh}
              isSyntheticMode={false}
              recentSecurityEvents={livePolling.data?.recentSecurityEvents ?? []}
              selectedRequestId={selectedRequestId}
              onSelectRequestId={setSelectedRequestId}
              detailRequest={liveDetail.request}
              detailLoading={liveDetail.isLoading}
              detailError={liveDetail.error}
              onDetailRetry={liveDetail.refetch}
            />
          ) : (
            <RequestsView
              activeTimeRange={timeRange}
              onSelectTimeRange={setTimeRange}
              shortcut={shortcut}
              onSelectShortcut={setShortcut}
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              decisionFilter={decisionFilter}
              onDecisionChange={setDecisionFilter}
              inspectionFilter={inspectionFilter}
              onInspectionChange={setInspectionFilter}
              sortField={sortField}
              onSortFieldChange={setSortField}
              sortOrder={sortOrder}
              onSortOrderChange={setSortOrder}
              page={page}
              onPageChange={setPage}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              items={tableItems}
              tableTotal={tableTotal}
              totalPages={totalPages}
              sidebarCounts={sidebarCounts}
              selectedRequestId={selectedRequestId}
              onSelectRequestId={setSelectedRequestId}
              detailRequest={liveDetail.request}
              detailLoading={liveDetail.isLoading}
              detailError={liveDetail.error}
              onDetailRetry={liveDetail.refetch}
              isLoading={isLoading}
              isRefreshing={isRefreshing}
              isStale={isStale}
              hasNewData={hasNewData}
              serverTime={serverTime}
              error={pollingError}
              onRefresh={handleRefresh}
              onApplyPendingData={livePolling.applyPendingData}
              isSyntheticMode={false}
            />
          )}
        </main>
      </div>
    </div>
  );
};
